import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';

// Stryde Research Logo URLs
const STRYDE_LOGO = "https://customer-assets.emergentagent.com/job_onboard-mastery/artifacts/2d1ii1ii_stryde-fin-01.jpg";
const STRYDE_ICON = "https://customer-assets.emergentagent.com/job_onboard-mastery/artifacts/x579wpam_stryde-fin-02.jpg";

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Stryde Branded */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden"
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400/30 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          {/* Stryde Logo */}
          <div className="flex items-center gap-4 mb-10">
            <img 
              src={STRYDE_ICON} 
              alt="Stryde" 
              className="w-16 h-16 object-contain bg-white rounded-xl p-2"
            />
            <div>
              <h1 className="font-heading font-bold text-3xl">Stryde<span className="text-amber-400">Mind</span></h1>
              <p className="text-blue-200 text-sm">Learning Management System</p>
            </div>
          </div>
          
          <h2 className="text-4xl font-heading font-bold mb-4 leading-tight">
            Empower Your Clinical Research Training
          </h2>
          <p className="text-lg text-blue-100 leading-relaxed max-w-md mb-8">
            A comprehensive learning management system designed for clinical research professionals. 
            Track progress, complete certifications, and stay compliant.
          </p>

          {/* Feature highlights */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
                <span className="text-amber-400 font-bold">01</span>
              </div>
              <span className="text-blue-100">Role-based curriculum assignments</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
                <span className="text-amber-400 font-bold">02</span>
              </div>
              <span className="text-blue-100">SOP quizzes with 80% pass requirement</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
                <span className="text-amber-400 font-bold">03</span>
              </div>
              <span className="text-blue-100">Certification expiry tracking</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-card">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-3 mb-4">
              <img 
                src={STRYDE_ICON} 
                alt="Stryde" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="font-heading font-bold text-xl text-blue-600">Stryde<span className="text-amber-500">Mind</span></h1>
              </div>
            </div>
            <CardTitle className="text-2xl font-heading font-bold text-slate-900">
              Welcome back
            </CardTitle>
            <CardDescription className="text-slate-500">
              Sign in to continue your learning journey
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 space-y-3">
              <div className="text-center">
                <p className="text-slate-600">
                  First time here?{' '}
                  <Link to="/set-password" className="text-blue-600 hover:text-blue-700 font-medium">
                    Set up your password
                  </Link>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
